/**
 * This TypeScript file defines the logic for the MMP File Upload component.
 * It includes:
 * - File selection and validation logic.
 * - File upload functionality using the ApiService.
 * - Toast notifications for success and error messages.
 * - Navigation to the user transcripts page after successful upload.
 */

import { Component, OnInit, OnDestroy } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { ApiService } from "src/app/services/api.service";
import { Subject, takeUntil } from "rxjs";
import { ExtractionService } from "src/app/shared/services/extraction.service";
import { CommonModule } from "@angular/common";
import { MmpMainHeaderComponent } from "../mmp-main-header/mmp-main-header.component";

@Component({
  selector: "app-mmp-file-upload",
  templateUrl: "./mmp-file-upload.component.html",
  styleUrls: ["./mmp-file-upload.component.scss"],
  standalone: true,
  imports: [CommonModule, MmpMainHeaderComponent],
})
export class MmpFileUploadComponent implements OnInit, OnDestroy {
  files: any[] = [];
  acceptedFormats = ".pdf,.docx";
  isLoading: boolean = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly apiService: ApiService,
    private readonly router: Router,
    private readonly extractionService: ExtractionService,
  ) {}

  ngOnInit(): void {
    this.extractionService.clearExtractionResult();
  }

  /**
   * Handles the file selection event triggered by the file input.
   * Validates the selected files against the accepted formats and
   * separates valid and invalid files.
   *
   * @param event - The file input change event.
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) {
      return;
    }

    const accepted = new Set(
      this.acceptedFormats
        .split(",")
        .map((ext) => ext.trim().toLowerCase()),
    );

    const selectedFiles = Array.from(input.files);

    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    for (const file of selectedFiles) {
      const fileExt = "." + (file.name.split(".").pop() || "").toLowerCase();

      if (accepted.has(fileExt)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    }

    if (invalidFiles.length > 0) {
      this.showToast(
        "toast-error",
        `The following files have invalid formats: ${invalidFiles.join(", ")}. 
       Allowed formats: ${this.acceptedFormats}`,
      );
      return;
    }

    this.files = validFiles;
    this.uploadFiles();
  }

  uploadFiles(): void {
    if (!this.files || this.files.length === 0) {
      this.showToast("toast-error", "No valid files to upload.");
      return;
    }

    this.isLoading = true;

    const formData = new FormData();
    this.files.forEach((file) => formData.append("files", file));

    this.apiService
      .uploadFiles(formData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isLoading = false;

          // Check if the response contains an error at index 0
          if (!!res?.length && !!res[0].error) {
            this.showToast(
              "toast-error",
              "Failed to upload files. Please reupload the file.",
            );
            this.files = []; // Clear the files array to allow reupload
            return;
          }

          setTimeout(() => {
            this.showToast("toast-success", "File uploaded successfully!");
          }, 1000);
          this.router.navigate(["/user-transcripts", 123], {
            state: { uploadData: res, origin: "upload" },
          });
        },
        error: (err) => {
          this.isLoading = false;
          this.showToast("toast-error", "Failed to upload files.");
        },
      });
  }

  /**
   * Displays a toast notification with the specified type and message.
   *
   * @param type - The type of the toast (e.g., "toast-success", "toast-error").
   * @param message - The message to display in the toast.
   */
  showToast(type: string, message: string) {
    this.snackBar.open(message, "x", {
      duration: 3000,
      horizontalPosition: "right",
      verticalPosition: "top",
      panelClass: [type],
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
